import React, { ReactNode, useState } from 'react';
import { X } from 'react-feather';
import cn from 'classnames';
import { ITableRecordData } from 'global.interfaces';
import { Actions } from '../TableActions';
import { TableActionDetails, TableBodyProps } from '../types';

export const TableBody = (props: TableBodyProps): JSX.Element => {
  const {
    data: records,
    columns,
    parentContext,
    actions,
    updateData,
    setUpdatingState,
    setCurrentRow,
    actionKey,
    additionalActionsClass,
    selectedRowsIds = [],
    toggleRowsSelection,
    openedRowDetailsId = null,
    toggleOpened,
    tableHasExtensions,
    tableRowDetails = () => null,
    selectedColumn,
    selectByColumnName,
    totals,
    withStickyAction,
  } = props;

  const [detailsActiveRow, setDetailsActiveRow] = useState('');

  const changeShowDetails = (key: string) => {
    setDetailsActiveRow(key);
  };

  const columnCount = actions ? columns.length + 1 : columns.length;
  const detailsAction =
    actions && (actions.find((action: any) => action.type === 'details') as TableActionDetails | null);
  const hasComponent = detailsAction && detailsAction.component;
  const DetailsComponent = detailsAction?.component;

  const getRecordValue = (record: ITableRecordData, key: string, isRenderFn: boolean): ReactNode => {
    const keys = key.split('.');

    const value = keys.reduce((xs, x) => {
      if (!xs) {
        return null;
      }

      return typeof xs === 'object' ? (xs as any)[x] : xs;
    }, record as any);

    if (isRenderFn) {
      return value;
    }

    return typeof value !== 'object' ? value : null;
  };

  const getRows = () => {
    return records.map((record, i) => {
      const key = `record_${i}`;
      const actionKeyReact = `action_${i}`;
      const recordId = record[selectByColumnName] as number;
      const recordIdArray = Array.of(recordId);
      const isSelected = selectedRowsIds?.includes(recordId);
      const isOpened = recordId === openedRowDetailsId;

      const handleOpenClick = () => {
        if (tableHasExtensions) {
          toggleOpened?.(recordId);
        }
      };
      const handleSelectChange = () => {
        if (tableHasExtensions && selectByColumnName) {
          toggleRowsSelection(recordIdArray);
        }
      };

      record.actionKey = actionKey;

      return (
        <React.Fragment key={key}>
          <tr
            className={cn({
              table__extendedRow: tableHasExtensions,
              table__extendedNewRow: !record.isRead && tableHasExtensions,
            })}>
            {columns.map((col) => {
              const colClassName = col.className ?? '';

              if (col.render) {
                return (
                  <td key={col.key} className={cn(colClassName, { 'table__sorted-row': col.key === selectedColumn })}>
                    <div
                      className={cn({
                        tabel__extendedRowEventWrapper: tableHasExtensions,
                      })}
                      onClick={handleOpenClick}>
                      {col.render(record, getRecordValue(record, col.key, true), parentContext)}
                    </div>
                  </td>
                );
              }

              return (
                <td key={col.key} className={col.className}>
                  {col.key === 'checkbox' ? (
                    <label className="table__checkbox">
                      <input
                        type="checkbox"
                        className="table__checkbox-input"
                        checked={isSelected}
                        onChange={handleSelectChange}
                      />
                      <span className="table__checkbox-label" />
                    </label>
                  ) : (
                    <div
                      className={cn({
                        tabel__extendedRowEventWrapper: tableHasExtensions,
                      })}
                      onClick={handleOpenClick}>
                      {getRecordValue(record, col.key, false)}
                    </div>
                  )}
                </td>
              );
            })}

            {actions && (
              <td className={cn({ 'table__sticky-action': withStickyAction })} key={actionKeyReact}>
                <Actions
                  actionList={actions}
                  data={record}
                  updateData={updateData}
                  setUpdatingState={setUpdatingState}
                  setCurrentRow={setCurrentRow}
                  changeShowDetails={() => changeShowDetails(key)}
                  additionalActionsClass={additionalActionsClass}
                />
              </td>
            )}
          </tr>

          {isOpened && (
            <tr>
              <td className="table__details" colSpan={columnCount}>
                <div className="table__details-inner">{tableRowDetails(recordId)}</div>
              </td>
            </tr>
          )}

          {detailsActiveRow === key && hasComponent && (
            <tr>
              <td className="table__details" colSpan={columnCount}>
                <div className="table__details-inner">
                  <X className="table__details-close" onClick={() => changeShowDetails('')} />
                  {DetailsComponent && <DetailsComponent data={record} />}
                </div>
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    });
  };

  const getTotalsRow = () => {
    if (!totals) {
      return null;
    }

    return (
      <React.Fragment>
        <tr className="table__body_total_amounts_row">
          {columns.map((col) => {
            return (
              <td key={col.key} className="table__body_total_amounts_column">
                <div>{getRecordValue(totals, col.key, false)}</div>
              </td>
            );
          })}
        </tr>
      </React.Fragment>
    );
  };

  return (
    <tbody>
      {getRows()}
      {getTotalsRow()}
    </tbody>
  );
};
